import { Store } from '@ngxs/store';
import { ParticleSystem } from '../models/particles/particle-system-types';
import { EngineParticlesState } from '../state/particles/particles.state';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { TweenParticleLookup } from 'src/app/shared/tween-particle-lookup';
import { StoryState } from 'src/app/story/state/story.state';
import { isEmpty } from 'lodash';
import { Subject, merge } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { ParticleActions } from '../state/particles/particles.actions';

declare const Human;

export class ParticleStateListener {
  private store: Store;
  private ngUnsubsribe = new Subject(); 

  constructor(store: Store) {
    this.store = store;
    this.listenForParticleChanges();
    this.listenForLookupChanges();
  }

  destroy() {
    this.ngUnsubsribe.next();
    this.ngUnsubsribe.complete();
  }

  private listenForLookupChanges() {
    this.store
      .select(EngineParticlesState.getTweenParticleLookup)
      .pipe(takeUntil(this.ngUnsubsribe))
      .subscribe((lookup) => {
        if (!isEmpty(lookup)) {
          TweenParticleLookup.refreshEngineLookup(
            this.store.selectSnapshot(StoryState.getStoryId),
            lookup
          );
        }
      });
  }

  private listenForParticleChanges() {
    const particleSystems$ = this.store.select(EngineParticlesState.getParticleSystems);
    const particleSystemsVisible$ = this.store.select(EngineParticlesState.getVisisbleParticleSystems);

    // track changes to systems and/or visiblity
    merge(
      particleSystems$,
      particleSystemsVisible$
    )    
      .pipe(takeUntil(this.ngUnsubsribe))
      .subscribe(() => {
      const activeChapterId = this.store.selectSnapshot(
        StoryChaptersState.getSelectedChapterId
      );

      const chapterParticles = this.store.selectSnapshot(
        EngineParticlesState.particleSystemsForChapter(activeChapterId)
      );

      const visibleParticleSystemsForChapter = this.store.selectSnapshot(
        EngineParticlesState.getVisibleParticleSystesmForChapter(activeChapterId)
      );

      // get rendered particle system for chapter
      const visibleChapterParticles = chapterParticles
        .filter((system) => {
          return visibleParticleSystemsForChapter.includes(system.id);
        });

      this.setEngineParticles(visibleChapterParticles);
    });
  }

  private setEngineParticles(particles: ParticleSystem[] = []) {
    // disable everything..
    // we can't destroy or disable by Id, because when a particle is deleted, the arg `particles` is []
    Human.particles.disableAll();
    Human.particles.resetAll();

    // copy particles to the engine
    const particleCopies = JSON.parse(JSON.stringify(particles));

    // Parse is async!
    Human.particles.parse(particleCopies, () => {
      
      // enable particles in this chapter
      particles.forEach((particle) => {
        Human.particles.enable(particle.id);
      });

      // assign to the active timeline.
      const canSetChapterParticles = Human.timeline.activeRoot != null;

      if (canSetChapterParticles) {
        Human.timeline.activeRoot._nowBranch.particleSystems = particles.map(
          (particle) => particle.id
        );
      }
    });
  }
}
